import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, BookOpen, Search, ShieldCheck, X, Filter } from "lucide-react";

const ENFORCED_BY_OPTIONS = ["frontend", "backend", "both"];
const ROLE_OPTIONS = ["admin", "team_lead", "agent", "qa", "all"];

const EMPTY_FORM = {
  title: "",
  description: "",
  module: "",
  applies_to: [],
  enforced_by: "backend",
  example: "",
};

const ROLE_BADGE_STYLES = {
  admin: "bg-red-50 text-red-700 border-red-200",
  team_lead: "bg-orange-50 text-orange-700 border-orange-200",
  agent: "bg-blue-50 text-blue-700 border-blue-200",
  qa: "bg-purple-50 text-purple-700 border-purple-200",
  all: "bg-slate-100 text-slate-700 border-slate-300",
};

const ENFORCED_BADGE_STYLES = {
  frontend: "bg-emerald-50 text-emerald-700 border-emerald-200",
  backend: "bg-sky-50 text-sky-700 border-sky-200",
  both: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AdminRuleBook() {
  const [rules, setRules] = useState([]);
  const [modules, setModules] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moduleFilter, setModuleFilter] = useState("__all__");
  const [roleFilter, setRoleFilter] = useState("__all__");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, obj = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rulebook");
      setRules(data.rules || []);
      setModules(data.modules || []);
      setRolesList(data.roles || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Client-side filtering keeps the whole set in memory and stays snappy
  // even at 100+ rules. Server-side filter still exists for role/module
  // deep-links via query params if we ever add them.
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (moduleFilter !== "__all__" && r.module !== moduleFilter) return false;
      if (roleFilter !== "__all__") {
        const roles = r.applies_to || [];
        if (!roles.includes(roleFilter) && !roles.includes("all")) return false;
      }
      if (s) {
        const hay = [r.title, r.description, r.module, r.example, ...(r.applies_to || [])].join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [rules, moduleFilter, roleFilter, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      if (!map.has(r.module)) map.set(r.module, []);
      map.get(r.module).push(r);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      title: rule.title || "",
      description: rule.description || "",
      module: rule.module || "",
      applies_to: rule.applies_to || [],
      enforced_by: rule.enforced_by || "backend",
      example: rule.example || "",
    });
    setDialogOpen(true);
  };

  const toggleRole = (role) => {
    setForm((f) => {
      const has = f.applies_to.includes(role);
      return { ...f, applies_to: has ? f.applies_to.filter((r) => r !== role) : [...f.applies_to, role] };
    });
  };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.module.trim()) {
      toast.error("Title, description, and module are required.");
      return;
    }
    if (form.applies_to.length === 0) {
      toast.error("Pick at least one role in Applies to (use 'all' for company-wide rules).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        module: form.module.trim(),
        applies_to: form.applies_to,
        enforced_by: form.enforced_by,
        example: form.example.trim(),
      };
      if (editing) {
        await api.put(`/rulebook/${editing.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/rulebook", payload);
        toast.success("Rule added");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.title}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/rulebook/${rule.id}`);
      toast.success("Rule deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const clearFilters = () => {
    setModuleFilter("__all__");
    setRoleFilter("__all__");
    setSearch("");
  };

  const filtersActive = moduleFilter !== "__all__" || roleFilter !== "__all__" || !!search.trim();

  return (
    <div className="space-y-6" data-testid="admin-rule-book">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-sm bg-slate-900 text-white grid place-items-center shrink-0">
            <BookOpen size={18} />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">CRM Rule Book</h2>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Single source of truth for every enforced rule in the CRM. Curated from the live codebase and fully editable — add your own SOPs alongside the seeded engineering rules.
            </p>
          </div>
        </div>
        <Button className="rounded-sm" onClick={openCreate} data-testid="add-rule-btn">
          <Plus size={14} className="mr-1.5" /> Add Rule
        </Button>
      </div>

      {/* Filter bar */}
      <div className="border border-border rounded-sm bg-muted/20 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Search</Label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search titles, descriptions, examples…"
              className="rounded-sm h-9 pl-8"
              data-testid="rule-search-input"
            />
          </div>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Module</Label>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="rounded-sm h-9" data-testid="rule-module-filter">
              <SelectValue placeholder="All modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All modules</SelectItem>
              {modules.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Applies to</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="rounded-sm h-9" data-testid="rule-role-filter">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All roles</SelectItem>
              {rolesList.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {filtersActive && (
          <Button
            variant="outline"
            className="rounded-sm h-9"
            onClick={clearFilters}
            data-testid="clear-filters-btn"
          >
            <X size={14} className="mr-1.5" /> Clear
          </Button>
        )}
        <div className="text-xs text-muted-foreground ml-auto flex items-center gap-1.5">
          <Filter size={12} />
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of {rules.length} rules
        </div>
      </div>

      {/* Rule list */}
      {loading ? (
        <div className="text-center text-sm text-muted-foreground py-16" data-testid="rule-loading">Loading rules…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-16 border border-dashed border-border rounded-sm" data-testid="rule-empty">
          {rules.length === 0 ? "No rules yet. Click 'Add Rule' to create the first one." : "No rules match the current filters."}
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([mod, list]) => (
            <section key={mod} data-testid={`rule-module-section-${mod}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <h3 className="font-display text-lg font-bold tracking-tight">{mod}</h3>
                <div className="flex-1 border-t border-border" />
                <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                  {list.length} rule{list.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="space-y-3">
                {list.map((rule) => (
                  <article
                    key={rule.id}
                    className="border border-border rounded-sm bg-background p-4 hover:border-slate-400 transition-colors"
                    data-testid={`rule-card-${rule.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <h4 className="font-semibold text-sm">{rule.title}</h4>
                          {rule.is_seeded && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-300 text-slate-600 bg-slate-50 inline-flex items-center gap-1">
                              <ShieldCheck size={9} /> Curated
                            </span>
                          )}
                          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${ENFORCED_BADGE_STYLES[rule.enforced_by] || ENFORCED_BADGE_STYLES.backend}`}>
                            {rule.enforced_by}
                          </span>
                          {(rule.applies_to || []).map((r) => (
                            <span
                              key={r}
                              className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${ROLE_BADGE_STYLES[r] || "bg-slate-50 text-slate-700 border-slate-200"}`}
                            >
                              {r}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-foreground/80 leading-relaxed">{rule.description}</p>
                        {rule.example && (
                          <div className="mt-2 pl-3 border-l-2 border-slate-200 text-xs text-muted-foreground italic leading-relaxed">
                            <span className="font-semibold not-italic text-foreground/60 mr-1">Example ›</span>
                            {rule.example}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-sm"
                          onClick={() => openEdit(rule)}
                          data-testid={`edit-rule-${rule.id}`}
                          title="Edit"
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => remove(rule)}
                          data-testid={`delete-rule-${rule.id}`}
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm sm:max-w-[640px]" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit rule" : "Add a new rule"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update the fields below and save. Changes are visible to every admin immediately." : "Document a rule so every admin sees the same source of truth."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. 'Bulk-assign is case-insensitive'"
                className="rounded-sm h-9"
                data-testid="rule-form-title"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Plain-English explanation of what the rule does and why it matters."
                rows={4}
                className="rounded-sm"
                data-testid="rule-form-description"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Module *</Label>
                <Input
                  value={form.module}
                  onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))}
                  placeholder="e.g. 'Bulk Upload'"
                  list="rule-module-suggestions"
                  className="rounded-sm h-9"
                  data-testid="rule-form-module"
                />
                <datalist id="rule-module-suggestions">
                  {modules.map((m) => <option key={m} value={m} />)}
                </datalist>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Enforced by *</Label>
                <Select value={form.enforced_by} onValueChange={(v) => setForm((f) => ({ ...f, enforced_by: v }))}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="rule-form-enforced-by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENFORCED_BY_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Applies to *</Label>
              <div className="flex flex-wrap gap-2" data-testid="rule-form-applies-to">
                {ROLE_OPTIONS.map((r) => {
                  const active = form.applies_to.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r)}
                      className={`text-xs uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-all ${active ? (ROLE_BADGE_STYLES[r] || "bg-slate-100 text-slate-700 border-slate-300") : "bg-transparent text-muted-foreground border-border hover:border-slate-400"}`}
                      data-testid={`rule-role-toggle-${r}`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Pick every role this rule affects. Use <code className="font-mono">all</code> for company-wide policies.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Example <span className="normal-case text-muted-foreground/60">(optional)</span></Label>
              <Textarea
                value={form.example}
                onChange={(e) => setForm((f) => ({ ...f, example: e.target.value }))}
                placeholder="A concrete scenario that makes the rule click."
                rows={3}
                className="rounded-sm"
                data-testid="rule-form-example"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialogOpen(false)} data-testid="rule-form-cancel">
              Cancel
            </Button>
            <Button className="rounded-sm" onClick={save} disabled={saving} data-testid="rule-form-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Add rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
